/**
 * EscalationTierExplainer - Visual horizontal stepper showing the 4-tier
 * notification escalation pipeline with the member's current cap highlighted.
 */

import React from 'react';
import { cn } from '@/components/ui/utils';

interface EscalationTierExplainerProps {
  /** The member's escalation cap: null=program default, 0=disabled, 1-4=capped at tier */
  memberCap: number | null;
  /** The program-level max tier (default 4) */
  programMax?: number;
  /** If provided, highlight the current active tier in red */
  activeTier?: number | null;
  /** Compact mode for inline usage in tables */
  compact?: boolean;
}

const TIERS = [
  { tier: 1, label: 'Initial', channels: 'in_app', interval: '' },
  { tier: 2, label: 'Follow-up', channels: 'in_app + email', interval: '48h' },
  { tier: 3, label: 'Urgent', channels: 'all channels', interval: '48h' },
  { tier: 4, label: 'Supervisor', channels: 'supervisor alert', interval: '48h' },
];

export const EscalationTierExplainer: React.FC<EscalationTierExplainerProps> = ({
  memberCap,
  programMax = 4,
  activeTier,
  compact = false,
}) => {
  // Effective cap: member override takes precedence, then program max
  const effectiveCap = memberCap !== null ? memberCap : programMax;

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        {TIERS.map((t) => {
          const isActive = activeTier !== null && activeTier !== undefined && activeTier === t.tier;
          const isBeyondCap = t.tier > effectiveCap;
          return (
            <div
              key={t.tier}
              className={cn(
                'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border',
                isActive
                  ? 'bg-red-500 border-red-600 text-white animate-pulse'
                  : isBeyondCap
                  ? 'bg-gray-100 border-gray-200 text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-600'
                  : 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
              )}
              title={`Tier ${t.tier}: ${t.label}${isBeyondCap ? ' (disabled)' : ''}`}
            >
              {t.tier}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Stepper */}
      <div className="flex items-center gap-0">
        {TIERS.map((t, idx) => {
          const isActive = activeTier !== null && activeTier !== undefined && activeTier === t.tier;
          const isBeyondCap = t.tier > effectiveCap;
          const isCapped = t.tier === effectiveCap && effectiveCap < programMax;

          return (
            <React.Fragment key={t.tier}>
              {/* Connector line */}
              {idx > 0 && (
                <div className="flex flex-col items-center flex-shrink-0 mx-1">
                  <span className={cn(
                    'text-[9px] font-mono mb-0.5',
                    isBeyondCap ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'
                  )}>
                    {t.interval}
                  </span>
                  <div className={cn(
                    'w-6 h-px',
                    isBeyondCap ? 'bg-gray-200 dark:bg-gray-700' : 'bg-blue-300 dark:bg-blue-600'
                  )} />
                </div>
              )}

              {/* Tier circle + label */}
              <div className="flex flex-col items-center min-w-[60px]">
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all',
                    isActive
                      ? 'bg-red-500 border-red-600 text-white ring-2 ring-red-300 animate-pulse'
                      : isBeyondCap
                      ? 'bg-gray-100 border-gray-200 text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-600'
                      : isCapped
                      ? 'bg-amber-50 border-amber-400 text-amber-700 ring-2 ring-amber-200 dark:bg-amber-900/30 dark:border-amber-500 dark:text-amber-300'
                      : 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                  )}
                >
                  {t.tier}
                </div>
                <span className={cn(
                  'text-[10px] mt-1 font-medium text-center leading-tight',
                  isBeyondCap ? 'text-gray-300 dark:text-gray-600' : 'text-gray-600 dark:text-gray-400'
                )}>
                  {t.label}
                </span>
                <span className={cn(
                  'text-[9px] text-center leading-tight',
                  isBeyondCap ? 'text-gray-200 dark:text-gray-700' : 'text-gray-400 dark:text-gray-500'
                )}>
                  {t.channels}
                </span>
                {isCapped && (
                  <span className="text-[9px] font-bold text-amber-600 dark:text-amber-400 mt-0.5">
                    CAP
                  </span>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Legend */}
      <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center">
        {effectiveCap === 0
          ? 'Escalation disabled for this member'
          : memberCap !== null
          ? `Member capped at Tier ${effectiveCap} (program allows up to Tier ${programMax})`
          : `Using program default: up to Tier ${programMax}`
        }
      </p>
    </div>
  );
};
